import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { Bucket, getSocketUrl } from "../../../api/config/apiConfig";
import {
  getConversations,
  getMessages,
  sendMessageAdmin,
  uploadChatFile,
  markReadAdmin,
  getSurveyConversation,
  getContacts,
  startConversation,
  sendPresenceHeartbeat,
  getPresenceMap,
  deleteConversationAdmin,
  deleteMessageAdmin,
} from "../../../api/services/communicationServices";
import {
  getAutoReplySettings,
  updateAutoReplySettings,
} from "../../../api/services/services";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  showDesktopBrowserNotification,
  requestDesktopNotificationPermission,
} from "../../../api/utils/browserNotificationHelper";
import "./style.css";

// ─── Web Audio Chime Helper ───────────────────────────────────────────────────
const playNotificationSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.12); // A5

    gain.gain.setValueAtTime(0.18, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime + 0.45);
  } catch (e) {
    // Ignore audio policy restrictions
  }
};

// ─── Utility helpers ─────────────────────────────────────────────────────────
const formatTime = (unix) => {
  if (!unix) return "";
  const d = new Date(unix * 1000);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (unix) => {
  if (!unix) return "";
  const d = new Date(unix * 1000);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return "Today";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const toStorageUrl = (urlOrKey) => {
  if (!urlOrKey || typeof urlOrKey !== "string") return null;
  const clean = urlOrKey.trim();
  if (!clean) return null;
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;
  const key = clean.startsWith("/") ? clean.substring(1) : clean;
  return `https://eu2.contabostorage.com/4e5c5b4868a5450aa89f1422b00b2d85:shelozon/${key}`;
};

const getFileIcon = (mimeType, fileName) => {
  if (!mimeType && !fileName) return "📄";
  const name = (fileName || "").toLowerCase();
  if (mimeType?.includes("pdf") || name.endsWith(".pdf")) return "📕";
  if (mimeType?.includes("word") || name.endsWith(".doc") || name.endsWith(".docx")) return "📘";
  if (mimeType?.includes("sheet") || name.endsWith(".xls") || name.endsWith(".xlsx") || name.endsWith(".csv")) return "📗";
  if (name.endsWith(".txt")) return "📝";
  return "📄";
};

const getFileUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${Bucket}${url}`;
};

// ─── Conversation List Item ───────────────────────────────────────────────────
const ConvItem = ({ conv, isActive, onClick, presence, onDelete }) => {
  const isSurvey = conv.type === "SURVEY" || !!conv.surveyId;
  const isAdminChat = conv.type === "ADMIN";
  const isSelfChat =
    (conv.title || "").toLowerCase().includes("note to self") ||
    (conv.title || "").toLowerCase().includes("message yourself");

  const getRoleBadge = () => {
    if (isSelfChat) return <span className="comm-role-chip" style={{ background: "#7c3aed", color: "#fff" }}>📱 Mobile Sync</span>;
    if (isAdminChat) return <span className="comm-role-chip role-admin">🛡️ Admin</span>;
    if (isSurvey) {
      const jobShort = conv.surveyId ? (conv.surveyId.length > 8 ? conv.surveyId.substring(0, 8) + "..." : conv.surveyId) : "Job";
      return <span className="comm-role-chip role-survey">📋 Job #{jobShort}</span>;
    }
    return <span className="comm-role-chip role-surveyor">👷 Surveyor</span>;
  };

  const getDisplayName = () => {
    if (isSelfChat) return "Note to Self (Mobile App)";
    if (isSurvey) {
      if (conv.assignmentName) {
        return conv.assignmentName;
      }
      const jobRef = conv.surveyId ? (conv.surveyId.length > 8 ? conv.surveyId.substring(0, 8) + "..." : conv.surveyId) : "Job";
      return `Job #${jobRef} · ${conv.surveyerName || "Surveyor"}`;
    }
    if (isAdminChat) {
      return conv.targetAdminName || conv.surveyerName || "Admin Member";
    }
    return conv.surveyerName || "Surveyor";
  };

  const displayName = getDisplayName();
  const userStatus = presence?.status || "OFFLINE";

  return (
    <div
      className={`comm-conv-item ${isActive ? "active" : ""}`}
      onClick={() => onClick(conv)}
      id={`conv-item-${conv.uuid}`}
    >
      <div style={{ position: "relative" }}>
        <div className={`conv-avatar ${isSelfChat ? "" : isAdminChat ? "avatar-admin" : isSurvey ? "avatar-survey" : ""}`} style={isSelfChat ? { background: "#7c3aed", color: "#fff" } : {}}>
          {isSelfChat ? "📱" : toStorageUrl(conv.surveyerAvatar || conv.targetAdminAvatar) ? (
            <img
              src={toStorageUrl(conv.surveyerAvatar || conv.targetAdminAvatar)}
              alt=""
              onError={(e) => { e.target.style.display = "none"; }}
              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            displayName[0].toUpperCase()
          )}
        </div>
        {!isSelfChat && (
          <span
            className={`presence-dot-avatar ${userStatus.toLowerCase()}`}
            title={`Status: ${userStatus}`}
          />
        )}
      </div>
      <div className="conv-info">
        <div className="conv-name">
          <span>{displayName}</span>
          {getRoleBadge()}
        </div>
        {isSurvey && conv.assignmentAddress && (
          <div style={{ fontSize: "11.5px", color: "#0284c7", display: "flex", alignItems: "center", gap: "3px", margin: "2px 0", fontWeight: 600 }}>
            📍 <span>{conv.assignmentAddress}</span>
          </div>
        )}
        <div className="conv-last-msg">{conv.lastMessage || "No messages yet"}</div>
      </div>
      <div className="conv-meta">
        <div className="conv-time">
          {conv.lastMessageAt ? formatTime(conv.lastMessageAt) : ""}
        </div>
        {conv.unreadAdminCount > 0 && (
          <div className="conv-unread-badge">{conv.unreadAdminCount}</div>
        )}
        {onDelete && (
          <button
            type="button"
            className="conv-delete-btn"
            title="Delete conversation"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(conv);
            }}
          >
            🗑️
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg, onImageClick, onDelete }) => {
  const isAdmin = msg.senderType === "ADMIN";
  const isSystem = msg.senderType === "SYSTEM";

  if (isSystem) {
    return (
      <div className="msg-system-pill">
        <span>{msg.content}</span>
      </div>
    );
  }

  return (
    <div className={`msg-bubble-wrap ${isAdmin ? "admin-side" : "surveyor-side"}`}>
      {!isAdmin && (
        <div className="msg-avatar-small">
          {toStorageUrl(msg.senderAvatar) ? (
            <img
              src={toStorageUrl(msg.senderAvatar)}
              alt=""
              onError={(e) => { e.target.style.display = "none"; }}
              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            (msg.senderName || "S")[0].toUpperCase()
          )}
        </div>
      )}
      {isAdmin && (
        <div className="msg-avatar-small admin" style={{ order: 2, marginLeft: "6px" }}>
          {toStorageUrl(msg.senderAvatar) ? (
            <img
              src={toStorageUrl(msg.senderAvatar)}
              alt=""
              onError={(e) => { e.target.style.display = "none"; }}
              style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            (msg.senderName || "A")[0].toUpperCase()
          )}
        </div>
      )}
      <div className={`msg-bubble ${isAdmin ? "bubble-admin" : "bubble-surveyor"}`}>
        <div className="msg-sender-header">
          <span className="msg-sender-name">{msg.senderName || (isAdmin ? "Admin" : "Surveyor")}</span>
          <span className={`comm-role-chip-mini ${isAdmin ? "role-admin" : "role-surveyor"}`}>
            {isAdmin ? "Admin" : "Surveyor"}
          </span>
          {onDelete && (
            <button
              type="button"
              className="msg-bubble-delete-btn"
              title="Delete message"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(msg);
              }}
            >
              🗑️
            </button>
          )}
        </div>

        {msg.messageType === "TEXT" && (
          <p className="msg-text">{msg.content}</p>
        )}

        {msg.messageType === "IMAGE" && (
          <div
            className="msg-image-wrap"
            onClick={() => onImageClick(getFileUrl(msg.fileUrl))}
          >
            <img
              src={getFileUrl(msg.fileUrl)}
              alt={msg.fileName || "Photo"}
              className="msg-image"
            />
            {msg.content && <p className="msg-caption">{msg.content}</p>}
          </div>
        )}

        {msg.messageType === "DOCUMENT" && (
          <a
            href={getFileUrl(msg.fileUrl)}
            target="_blank"
            rel="noreferrer"
            className="msg-doc-card"
          >
            <div className="msg-doc-icon">{getFileIcon(msg.mimeType, msg.fileName)}</div>
            <div className="msg-doc-info">
              <div className="msg-doc-name">{msg.fileName || "Document"}</div>
              <div className="msg-doc-size">{msg.fileSize || ""} · Tap to download</div>
            </div>
            <div className="msg-doc-download">⬇️</div>
          </a>
        )}

        <div className="msg-meta">
          <span className="msg-time">{formatTime(msg.createdAt)}</span>
          {isAdmin && (
            <span
              className={`msg-read-tick ${msg.isRead ? "read" : "delivered"}`}
              title={msg.isRead ? (msg.readAt ? `Read ${formatTime(msg.readAt)}` : "Read") : "Delivered"}
            >
              {msg.isRead ? "✓✓" : "✓"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Date Separator ───────────────────────────────────────────────────────────
const DateSeparator = ({ unix }) => (
  <div className="msg-date-sep">
    <span>{formatDate(unix)}</span>
  </div>
);

// ─── Image Lightbox ───────────────────────────────────────────────────────────
const Lightbox = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" onClick={onClose}>✕</button>
      <img
        src={src}
        alt="Preview"
        className="lightbox-image"
        onClick={(e) => e.stopPropagation()}
      />
      <a
        href={src}
        download
        className="lightbox-download"
        onClick={(e) => e.stopPropagation()}
      >
        ⬇ Download Full Image
      </a>
    </div>
  );
};

// ─── New Conversation Modal ───────────────────────────────────────────────────
const NewChatModal = ({ isOpen, onClose, onSelectContact }) => {
  const [activeTab, setActiveTab] = useState("surveyors");
  const [contacts, setContacts] = useState({ surveyors: [], admins: [] });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      getContacts()
        .then((res) => {
          const data = res?.data?.data;
          setContacts({
            surveyors: Array.isArray(data?.surveyors) ? data.surveyors : [],
            admins: Array.isArray(data?.admins) ? data.admins : [],
          });
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load contacts:", err);
          setError("Failed to load contacts. Please verify your internet connection.");
          setLoading(false);
        });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentList = activeTab === "surveyors" ? contacts.surveyors : contacts.admins;
  const filteredList = (currentList || []).filter(
    (c) =>
      (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="new-chat-modal-overlay" onClick={onClose}>
      <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="new-chat-modal-header">
          <div>
            <h3>Start New Conversation</h3>
            <p>Select a surveyor or admin team member to chat with</p>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="new-chat-tabs">
          <button
            className={`new-chat-tab ${activeTab === "surveyors" ? "active" : ""}`}
            onClick={() => setActiveTab("surveyors")}
          >
            👷 Surveyors ({contacts.surveyors.length})
          </button>
          <button
            className={`new-chat-tab ${activeTab === "admins" ? "active" : ""}`}
            onClick={() => setActiveTab("admins")}
          >
            🛡️ Admin Team ({contacts.admins.length})
          </button>
        </div>

        <div className="new-chat-search-wrap">
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="new-chat-search-input"
            autoFocus
          />
        </div>

        <div className="new-chat-list">
          {loading ? (
            <div className="new-chat-empty">Loading contacts...</div>
          ) : error ? (
            <div className="new-chat-empty" style={{ color: "#ef4444" }}>{error}</div>
          ) : filteredList.length === 0 ? (
            <div className="new-chat-empty">No contacts found</div>
          ) : (
            filteredList.map((contact) => {
              const isSelf = contact.isSelf === true;
              return (
                <div
                  key={contact.uuid}
                  className={`new-chat-contact-item ${isSelf ? "self-contact-item" : ""}`}
                  style={isSelf ? { background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "8px" } : {}}
                  onClick={() => onSelectContact(activeTab === "surveyors" ? "DIRECT" : "ADMIN", contact)}
                >
                  <div style={{ position: "relative" }}>
                    <div
                      className={`contact-avatar ${isSelf ? "self-avatar" : activeTab === "admins" ? "admin-avatar" : ""}`}
                      style={isSelf ? { background: "#7c3aed", color: "#fff" } : {}}
                    >
                      {isSelf ? "📱" : (contact.name || contact.email || "U")[0].toUpperCase()}
                    </div>
                    {!isSelf && (
                      <span
                        className={`presence-dot-avatar ${(contact.presence?.status || "offline").toLowerCase()}`}
                        title={`Status: ${contact.presence?.status || "OFFLINE"}`}
                      />
                    )}
                  </div>
                  <div className="contact-details">
                    <div className="contact-name" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>{contact.name || contact.email || "User"}</span>
                      {isSelf && (
                        <span style={{ fontSize: "10px", padding: "2px 6px", borderRadius: "4px", background: "#7c3aed", color: "#fff", fontWeight: 700 }}>
                          Message Yourself
                        </span>
                      )}
                    </div>
                    <div className="contact-email">
                      {isSelf ? `Sync notes & messages directly with your mobile app (${contact.email})` : contact.email}
                    </div>
                    {contact.phone && <div className="contact-phone">📞 {contact.phone}</div>}
                  </div>
                  <button
                    type="button"
                    className="contact-action-btn"
                    style={isSelf ? { background: "#7c3aed", borderColor: "#7c3aed" } : {}}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectContact(activeTab === "surveyors" ? "DIRECT" : "ADMIN", contact);
                    }}
                  >
                    {isSelf ? "Sync ➔" : "Chat ➔"}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ─── WhatsApp-style Automated Message Modal ─────────────────────────────────
const AutomatedMessageModal = ({ isOpen, onClose }) => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [message, setMessage] = useState("");
  const [cooldownMinutes, setCooldownMinutes] = useState(60);
  const [updatedBy, setUpdatedBy] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      getAutoReplySettings()
        .then((res) => {
          const data = res?.data?.data;
          if (data) {
            setIsEnabled(data.isEnabled !== false);
            setMessage(data.message || "");
            setCooldownMinutes(data.cooldownMinutes || 60);
            setUpdatedBy(data.updatedBy || "");
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to load auto-reply settings:", err);
          setLoading(false);
        });
    }
  }, [isOpen]);

  const insertPlaceholder = (tag) => {
    if (!textareaRef.current) {
      setMessage((prev) => (prev ? prev + " " + tag : tag));
      return;
    }
    const start = textareaRef.current.selectionStart || message.length;
    const end = textareaRef.current.selectionEnd || message.length;
    const nextMsg = message.substring(0, start) + " " + tag + " " + message.substring(end);
    setMessage(nextMsg);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + tag.length + 2;
      }
    }, 50);
  };

  const handleSave = () => {
    setSaving(true);
    updateAutoReplySettings({
      isEnabled,
      message,
      cooldownMinutes: Number(cooldownMinutes),
    })
      .then(() => {
        setSaving(false);
        setToast({ type: "success", text: "Automated Message settings saved successfully!" });
        setTimeout(() => {
          setToast(null);
          onClose();
        }, 1200);
      })
      .catch((err) => {
        setSaving(false);
        setToast({
          type: "error",
          text: err?.response?.data?.message || "Failed to update automated message settings.",
        });
        setTimeout(() => setToast(null), 3500);
      });
  };

  const templates = [
    {
      title: "Standard Support Greeting",
      text: "Hello {surveyor_name}! Thank you for reaching out to Shelozon Support. We have received your message and an administrator will respond to you shortly.",
    },
    {
      title: "Survey Update Acknowledged",
      text: "Hello {surveyor_name}, thank you for your survey update. Our dispatch team is reviewing your message and will provide next steps right away.",
    },
    {
      title: "After-Hours Notice",
      text: "Hi {surveyor_name}, thank you for reaching out. We have logged your message into Shelozon Central Dispatch and an admin will connect with you as soon as possible.",
    },
  ];

  if (!isOpen) return null;

  // Live preview text with sample values
  const previewText = (message || "Hello! Thank you for reaching out to Shelozon Support. We have received your message and an administrator will respond to you shortly.")
    .replace(/{surveyor_name}/gi, "John")
    .replace(/{survey_id}/gi, "SURV-8492")
    .replace(/{date}/gi, new Date().toISOString().split("T")[0]);

  return (
    <div className="new-chat-modal-overlay" onClick={onClose}>
      <div
        className="auto-reply-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "780px", width: "95%" }}
      >
        {/* Header */}
        <div className="new-chat-modal-header" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
              }}
            >
              🤖
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 800, color: "#0f172a" }}>
                  Automated Message Settings
                </h3>
                <span
                  style={{
                    background: "#fef3c7",
                    color: "#92400e",
                    fontSize: "11px",
                    fontWeight: 700,
                    padding: "3px 8px",
                    borderRadius: "6px",
                    border: "1px solid #fde68a",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  👑 Super Admin
                </span>
              </div>
              <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#64748b" }}>
                Similar to WhatsApp, surveyors automatically receive this predefined message upon messaging.
              </p>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
            Loading automated message settings...
          </div>
        ) : (
          <div style={{ padding: "20px 24px", maxHeight: "75vh", overflowY: "auto" }}>
            {/* Status Toast */}
            {toast && (
              <div
                style={{
                  padding: "10px 16px",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  fontSize: "13px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: toast.type === "success" ? "#ecfdf5" : "#fef2f2",
                  color: toast.type === "success" ? "#065f46" : "#991b1b",
                  border: `1px solid ${toast.type === "success" ? "#a7f3d0" : "#fecaca"}`,
                }}
              >
                {toast.type === "success" ? "✓" : "⚠️"} {toast.text}
              </div>
            )}

            {/* Toggle Card */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 18px",
                background: isEnabled ? "#f0fdf4" : "#f8fafc",
                border: `1.5px solid ${isEnabled ? "#86efac" : "#e2e8f0"}`,
                borderRadius: "12px",
                marginBottom: "20px",
                transition: "all 0.2s ease",
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>
                    Automated Auto-Reply Status
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: "12px",
                      background: isEnabled ? "#22c55e" : "#94a3b8",
                      color: "#fff",
                    }}
                  >
                    {isEnabled ? "ACTIVE" : "DISABLED"}
                  </span>
                </div>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                  When enabled, any surveyor message automatically receives this predefined greeting.
                </div>
              </div>
              <label className="comm-toggle-switch">
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                />
                <span className="comm-toggle-slider" />
              </label>
            </div>

            {/* Two Column Layout: Editor on left, WhatsApp Preview on right */}
            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "20px" }}>
              {/* Left Column: Form & Placeholders */}
              <div>
                <div style={{ marginBottom: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: "6px",
                    }}
                  >
                    Custom Automated Message Text
                  </label>
                  <textarea
                    ref={textareaRef}
                    rows={5}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your automated response message here..."
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      borderRadius: "10px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "13px",
                      fontFamily: "inherit",
                      lineHeight: "1.5",
                      resize: "vertical",
                      boxSizing: "border-box",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#10b981")}
                    onBlur={(e) => (e.target.style.borderColor = "#cbd5e1")}
                  />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "11px",
                      color: "#94a3b8",
                      marginTop: "4px",
                    }}
                  >
                    <span>Click tags below to insert dynamic values</span>
                    <span>{message.length} characters</span>
                  </div>
                </div>

                {/* Variable Tags */}
                <div style={{ marginBottom: "16px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Dynamic Tags:
                  </span>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                    {[
                      { tag: "{surveyor_name}", label: "👷 Surveyor Name" },
                      { tag: "{survey_id}", label: "📋 Survey ID" },
                      { tag: "{date}", label: "📅 Current Date" },
                    ].map((item) => (
                      <button
                        key={item.tag}
                        type="button"
                        onClick={() => insertPlaceholder(item.tag)}
                        style={{
                          background: "#ecfdf5",
                          border: "1px solid #a7f3d0",
                          color: "#047857",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                        title={`Click to insert ${item.tag}`}
                      >
                        + {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quick Templates */}
                <div style={{ marginBottom: "18px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                    Quick Predefined Templates:
                  </span>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "6px" }}>
                    {templates.map((tpl) => (
                      <button
                        key={tpl.title}
                        type="button"
                        onClick={() => setMessage(tpl.text)}
                        style={{
                          textAlign: "left",
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 10px",
                          fontSize: "12px",
                          color: "#334155",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f1f5f9";
                          e.currentTarget.style.borderColor = "#cbd5e1";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#f8fafc";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                      >
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{tpl.title}</div>
                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tpl.text}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Response Cooldown Setting */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#1e293b",
                      marginBottom: "6px",
                    }}
                  >
                    Reply Frequency & Cooldown
                  </label>
                  <select
                    value={cooldownMinutes}
                    onChange={(e) => setCooldownMinutes(Number(e.target.value))}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "13px",
                      color: "#0f172a",
                      background: "#fff",
                      outline: "none",
                    }}
                  >
                    <option value={15}>Every 15 minutes of inactivity</option>
                    <option value={30}>Every 30 minutes of inactivity</option>
                    <option value={60}>Every 1 hour (Default & Recommended)</option>
                    <option value={360}>Every 6 hours</option>
                    <option value={1440}>Once every 24 hours per surveyor</option>
                  </select>
                  <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                    Prevents sending repetitive automated replies for consecutive sentences sent in the same chat session.
                  </div>
                </div>
              </div>

              {/* Right Column: WhatsApp Live Preview */}
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1e293b",
                    marginBottom: "6px",
                  }}
                >
                  WhatsApp-Style Live Chat Preview
                </label>
                <div
                  style={{
                    background: "#efeae2",
                    borderRadius: "14px",
                    border: "1px solid #d1d5db",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* WhatsApp Chat Header */}
                  <div
                    style={{
                      background: "#075e54",
                      color: "#fff",
                      padding: "10px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "50%",
                        background: "#128c7e",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                      }}
                    >
                      🏢
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: "13px", fontWeight: 700, lineHeight: 1.2 }}>
                        Shelozon Support
                      </div>
                      <div style={{ fontSize: "10px", color: "#dcf8c6", lineHeight: 1.2 }}>
                        Automated Bot • Online
                      </div>
                    </div>
                  </div>

                  {/* WhatsApp Chat Body */}
                  <div
                    style={{
                      padding: "16px 12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      minHeight: "240px",
                      background: "#efeae2",
                      backgroundImage: "radial-gradient(#d4cebf 1px, transparent 1px)",
                      backgroundSize: "16px 16px",
                    }}
                  >
                    {/* Incoming message from surveyor */}
                    <div
                      style={{
                        alignSelf: "flex-start",
                        maxWidth: "85%",
                        background: "#ffffff",
                        padding: "8px 12px",
                        borderRadius: "8px 8px 8px 0px",
                        fontSize: "12px",
                        color: "#111827",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        position: "relative",
                      }}
                    >
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#059669", marginBottom: "2px" }}>
                        John (Surveyor)
                      </div>
                      Hi Admin, I have reached the site and started the survey.
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#9ca3af",
                          textAlign: "right",
                          marginTop: "2px",
                        }}
                      >
                        10:45 AM
                      </div>
                    </div>

                    {/* Automated message outgoing */}
                    <div
                      style={{
                        alignSelf: "flex-end",
                        maxWidth: "85%",
                        background: "#dcf8c6",
                        padding: "8px 12px",
                        borderRadius: "8px 8px 0px 8px",
                        fontSize: "12px",
                        color: "#111827",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        lineHeight: "1.4",
                      }}
                    >
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#075e54", marginBottom: "2px", display: "flex", alignItems: "center", gap: "4px" }}>
                        <span>⚡ Automated Reply</span>
                      </div>
                      {previewText}
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#6b7280",
                          textAlign: "right",
                          marginTop: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "3px",
                        }}
                      >
                        <span>10:45 AM</span>
                        <span style={{ color: "#34b7f1", fontWeight: 800 }}>✓✓</span>
                      </div>
                    </div>
                  </div>
                </div>

                {updatedBy && (
                  <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "8px", textAlign: "right" }}>
                    Last updated by: <strong>{updatedBy}</strong>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Buttons */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "24px",
                paddingTop: "16px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "9px 18px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                  color: "#475569",
                  fontWeight: 600,
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: "9px 24px",
                  borderRadius: "8px",
                  border: "none",
                  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "13px",
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
                }}
              >
                {saving ? "Saving..." : "✓ Save Automated Message"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
const CommunicationHub = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAutoReplyModalOpen, setIsAutoReplyModalOpen] = useState(false);
  const [typingUser, setTypingUser] = useState(null);
  const [inAppBanner, setInAppBanner] = useState(null);
  const bannerTimerRef = useRef(null);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== "undefined" && "Notification" in window ? Notification.permission : "default"
  );
  const [presenceMap, setPresenceMap] = useState({});

  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const messagesEndRef = useRef(null);
  const streamContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const pollingRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const activeConvRef = useRef(activeConv);
  useEffect(() => {
    activeConvRef.current = activeConv;
  }, [activeConv]);

  // ── Scroll to bottom helper ──────────────────────────────────────────────
  const scrollToBottom = useCallback((smooth = true) => {
    if (streamContainerRef.current) {
      streamContainerRef.current.scrollTo({
        top: streamContainerRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" });
    }
  }, []);

  const handleStreamScroll = () => {
    if (!streamContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = streamContainerRef.current;
    const isScrolledUp = scrollHeight - scrollTop - clientHeight > 180;
    setShowScrollBottom(isScrolledUp);
  };

  const loadedConvIdRef = useRef(null);

  // ── Load conversations ───────────────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    try {
      const res = await getConversations();
      const rawData = res?.data?.data;
      const list = Array.isArray(rawData) ? rawData : (rawData?.conversations || []);
      setConversations(list);
      // If active conversation was opened with partial metadata (e.g. from notification), enrich with full details
      if (activeConvRef.current?.uuid) {
        const full = list.find((c) => c.uuid === activeConvRef.current.uuid);
        if (full) {
          setActiveConv((prev) => ({ ...full, ...prev, surveyDetail: full.surveyDetail || prev?.surveyDetail }));
        }
      }
    } catch (e) {
      console.error("Failed to load conversations", e);
    }
  }, []);

  // ── Load messages for active conversation ───────────────────────────────
  const loadMessages = useCallback(async (convId, force = false) => {
    if (!convId) return;
    if (!force && loadedConvIdRef.current === convId) return;
    loadedConvIdRef.current = convId;

    setLoading(true);
    try {
      const res = await getMessages(convId);
      const rawData = res?.data?.data;
      const fetchedMessages = Array.isArray(rawData) ? rawData : (rawData?.messages || []);
      setMessages(fetchedMessages);

      const hasUnread = fetchedMessages.some((m) => !m.isRead && m.senderType === "SURVEYOR");
      if (hasUnread) {
        await markReadAdmin(convId);
        setConversations((prev) =>
          prev.map((c) =>
            c.uuid === convId ? { ...c, unreadAdminCount: 0 } : c
          )
        );
      }

      // Join WebSocket room
      if (socketRef.current) {
        socketRef.current.emit("joinConversation", convId);
      }
    } catch (e) {
      console.error("Failed to load messages", e);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Presence Heartbeat & Sync ─────────────────────────────────────────────
  const fetchPresence = useCallback(async () => {
    try {
      const res = await getPresenceMap();
      if (res?.data?.data) {
        setPresenceMap((prev) => ({ ...prev, ...res.data.data }));
      }
    } catch (e) {
      // Silently catch
    }
  }, []);

  // ── Show In-App Banner & Trigger Desktop Push Notification ───────────────
  const triggerMessageNotification = (msg) => {
    // Avoid self notification
    let currentAdminId = null;
    try {
      const ud = JSON.parse(localStorage.getItem("shelUserData") || "{}");
      currentAdminId = ud?.uuid || ud?.id;
    } catch (_) {}
    if (currentAdminId && msg.senderId === currentAdminId) return;

    playNotificationSound();
    const sender = msg.senderName || (msg.senderType === "ADMIN" ? "Admin" : "Surveyor");
    const role = msg.senderType === "ADMIN" ? "Admin" : "Surveyor";
    const body =
      msg.messageType === "IMAGE"
        ? "Sent a photo 📷"
        : msg.messageType === "DOCUMENT"
        ? `Sent a document 📄 ${msg.fileName || ""}`
        : msg.content || "New message";

    setInAppBanner({
      conversationId: msg.conversationId,
      senderName: sender,
      role,
      body,
      surveyId: msg.surveyId,
    });

    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setInAppBanner(null), 6000);

    // Desktop / Background Browser Notification (persists even if window minimized or tab inactive)
    showDesktopBrowserNotification({
      title: `New message from ${sender} (${role})`,
      body,
      conversationId: msg.conversationId,
      senderName: sender,
      surveyId: msg.surveyId,
      url: "/communication",
    });
  };

  // ── Request Browser Notification Permission ──────────────────────────────
  const requestNotificationPermission = async () => {
    const perm = await requestDesktopNotificationPermission();
    setNotifPermission(perm);
  };

  // ── Periodic Polling for Conversations List ──────────────────────────────
  useEffect(() => {
    loadConversations();
    pollingRef.current = setInterval(loadConversations, 8000);
    return () => clearInterval(pollingRef.current);
  }, [loadConversations]);

  // ── Load messages when activeConv changes ────────────────────────────────
  useEffect(() => {
    if (activeConv?.uuid) {
      loadMessages(activeConv.uuid);
    } else {
      loadedConvIdRef.current = null;
      setMessages([]);
    }
  }, [activeConv?.uuid, loadMessages]);

  // ── Auto-scroll when messages update ─────────────────────────────────────
  useEffect(() => {
    scrollToBottom(true);
  }, [messages, scrollToBottom]);

  // ── Presence Heartbeat & Sync Effect ─────────────────────────────────────
  useEffect(() => {
    fetchPresence();
    sendPresenceHeartbeat("ACTIVE");

    const heartbeatTimer = setInterval(() => {
      sendPresenceHeartbeat(document.hidden ? "IDLE" : "ACTIVE");
      fetchPresence();
    }, 25000);

    const handleFocus = () => sendPresenceHeartbeat("ACTIVE");
    const handleBlur = () => sendPresenceHeartbeat("IDLE");
    const handleVisibilityChange = () => {
      sendPresenceHeartbeat(document.hidden ? "IDLE" : "ACTIVE");
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(heartbeatTimer);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchPresence]);

  // ── Setup WebSocket Connection ───────────────────────────────────────────
  useEffect(() => {
    const socketUrl = getSocketUrl("communication");

    const socket = io(socketUrl, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
      timeout: 5000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("%c[CommunicationHub] 🟢 WebSocket Connected | ID: " + socket.id, "color: #10B981; font-weight: bold;");
      socket.emit("joinAdmin");
      if (activeConvRef.current?.uuid) {
        socket.emit("joinConversation", activeConvRef.current.uuid);
      }
    });

    socket.on("newMessage", (msg) => {
      console.log("%c[CommunicationHub] 📩 New Message via WebSocket:", "color: #6366F1; font-weight: bold;", {
        id: msg?.uuid,
        conversationId: msg?.conversationId,
        sender: msg?.senderName,
        type: msg?.messageType,
        content: msg?.content,
      });

      let currentAdminId = null;
      try {
        const ud = JSON.parse(localStorage.getItem("shelUserData") || "{}");
        currentAdminId = ud?.uuid || ud?.id;
      } catch (_) {}
      if (currentAdminId && msg?.senderId === currentAdminId) {
        return;
      }

      const currentActive = activeConvRef.current;
      if (currentActive && msg.conversationId === currentActive.uuid) {
        setMessages((prev) => {
          if (prev.some((m) => m.uuid === msg.uuid)) return prev;
          return [...prev, msg];
        });
        markReadAdmin(currentActive.uuid);
        scrollToBottom(true);

        // Keep active conversation at the top of conversation list with fresh snippet
        setConversations((prev) => {
          const updated = prev.map((c) =>
            c.uuid === msg.conversationId
              ? {
                  ...c,
                  lastMessage: msg.content || (msg.messageType === "IMAGE" ? "Photo 📷" : "Document 📄"),
                  lastMessageAt: msg.createdAt || Math.floor(Date.now() / 1000),
                  unreadAdminCount: 0,
                }
              : c
          );
          return [...updated].sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
        });

        // If user is minimized or in another tab, still fire desktop browser notification
        if (document.hidden) {
          const sender = msg.senderName || (msg.senderType === "ADMIN" ? "Admin" : "Surveyor");
          const role = msg.senderType === "ADMIN" ? "Admin" : "Surveyor";
          const body =
            msg.messageType === "IMAGE"
              ? "Sent a photo 📷"
              : msg.messageType === "DOCUMENT"
              ? `Sent a document 📄 ${msg.fileName || ""}`
              : msg.content || "New message";

          showDesktopBrowserNotification({
            title: `New message from ${sender} (${role})`,
            body,
            conversationId: msg.conversationId,
            senderName: sender,
            surveyId: msg.surveyId,
            url: "/communication",
          });
        }
      } else {
        // Refresh conversations list to update unread badge
        loadConversations();
        triggerMessageNotification(msg);
      }
    });

    socket.on("messagesRead", (data) => {
      console.log("%c[CommunicationHub] 👁️ Messages Read via WebSocket:", "color: #0EA5E9; font-weight: bold;", data);
      const currentActive = activeConvRef.current;
      if (currentActive && currentActive.uuid === data.conversationId) {
        setMessages((prev) => {
          const hasUnread = prev.some((m) => !m.isRead);
          if (!hasUnread) return prev;
          return prev.map((m) => (m.isRead ? m : { ...m, isRead: true, readAt: data.readAt }));
        });
      }
      setConversations((prev) => {
        const target = prev.find((c) => c.uuid === data.conversationId);
        if (!target || target.unreadAdminCount === 0) return prev;
        return prev.map((c) =>
          c.uuid === data.conversationId
            ? { ...c, unreadAdminCount: data.by === "ADMIN" ? 0 : c.unreadAdminCount }
            : c
        );
      });
    });

    socket.on("presence:update", (data) => {
      console.log("%c[CommunicationHub] 🟢 Presence Update:", "color: #10B981; font-weight: bold;", data);
      if (data?.userId) {
        setPresenceMap((prev) => ({
          ...prev,
          [data.userId]: {
            status: data.status,
            lastSeen: data.lastSeen,
            role: data.role,
            name: data.name,
          },
        }));
      }
    });

    socket.on("conversation:deleted", (data) => {
      console.log("%c[CommunicationHub] 🗑️ Conversation Deleted:", "color: #EF4444; font-weight: bold;", data);
      if (data?.conversationId) {
        setConversations((prev) => prev.filter((c) => c.uuid !== data.conversationId));
        if (activeConvRef.current?.uuid === data.conversationId) {
          setActiveConv(null);
        }
      }
    });

    socket.on("messageDeleted", (data) => {
      console.log("%c[CommunicationHub] 🗑️ Message Deleted:", "color: #EF4444; font-weight: bold;", data);
      if (data?.messageId) {
        setMessages((prev) => prev.filter((m) => m.uuid !== data.messageId));
      }
    });

    socket.on("userTyping", (data) => {
      console.log("%c[CommunicationHub] ✍️ User Typing:", "color: #F59E0B; font-weight: bold;", data);
      if (data?.conversationId && activeConvRef.current?.uuid !== data.conversationId) {
        return;
      }
      setTypingUser(data.senderName || "Surveyor");
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setTypingUser(null);
      }, 3000);
    });

    return () => {
      socket.disconnect();
    };
  }, [loadConversations, scrollToBottom]);

  const [deleteConvModalTarget, setDeleteConvModalTarget] = useState(null);
  const [deleteMsgModalTarget, setDeleteMsgModalTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Delete conversation handler (opens confirmation dialog) ──────────────
  const handleDeleteConversation = (conv) => {
    if (!conv?.uuid) return;
    setDeleteConvModalTarget(conv);
  };

  // ── Confirm delete conversation ──────────────────────────────────────────
  const confirmDeleteConversation = async () => {
    if (!deleteConvModalTarget?.uuid) return;
    const targetId = deleteConvModalTarget.uuid;
    setIsDeleting(true);
    try {
      // Optimistic removal from UI
      setConversations((prev) => prev.filter((c) => c.uuid !== targetId));
      if (activeConv?.uuid === targetId) {
        setActiveConv(null);
      }
      await deleteConversationAdmin(targetId);
    } catch (err) {
      console.error("Failed to delete conversation:", err);
      loadConversations();
    } finally {
      setIsDeleting(false);
      setDeleteConvModalTarget(null);
    }
  };

  // ── Delete individual message handler (opens confirmation dialog) ────────
  const handleDeleteMessage = (msg) => {
    if (!msg?.uuid) return;
    setDeleteMsgModalTarget(msg);
  };

  // ── Confirm delete message ───────────────────────────────────────────────
  const confirmDeleteMessage = async () => {
    if (!deleteMsgModalTarget?.uuid) return;
    const targetId = deleteMsgModalTarget.uuid;
    setIsDeleting(true);
    try {
      // Optimistic removal from UI
      setMessages((prev) => prev.filter((m) => m.uuid !== targetId));
      await deleteMessageAdmin(targetId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    } finally {
      setIsDeleting(false);
      setDeleteMsgModalTarget(null);
    }
  };

  // ── Handle incoming navigation state (e.g. from Notifications or Survey Details) ──
  useEffect(() => {
    if (!location.state) return;

    if (location.state.conversation) {
      setActiveConv(location.state.conversation);
    } else if (location.state.conversationId) {
      const convId = location.state.conversationId;
      if (activeConvRef.current?.uuid !== convId) {
        const found = conversations.find((c) => c.uuid === convId);
        if (found) {
          setActiveConv(found);
        } else {
          setActiveConv({
            uuid: convId,
            surveyerName: location.state.senderName || "Contact",
            type: location.state.senderType === "ADMIN" ? "ADMIN" : "DIRECT",
            surveyId: location.state.surveyId,
          });
        }
      }
    } else if (location.state.surveyId && location.state.surveyerId) {
      getSurveyConversation(
        location.state.surveyId,
        location.state.surveyerId,
        location.state.surveyerName || "Surveyor"
      )
        .then((res) => {
          if (res?.data?.data) {
            setActiveConv(res.data.data);
          }
        })
        .catch((err) => console.error("Error auto-opening survey conversation:", err));
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, navigate, location.pathname, conversations]);

  // ── Send text message ────────────────────────────────────────────────────
  const handleSendText = async () => {
    const trimmed = text.trim();
    if (!trimmed || !activeConv || sending) return;

    setText("");
    setSending(true);

    try {
      const res = await sendMessageAdmin(activeConv.uuid, {
        messageType: "TEXT",
        content: trimmed,
      });

      const newMsg = res?.data?.data;
      if (newMsg) {
        console.log("%c[CommunicationHub] 📤 Sent Text Message:", "color: #10B981; font-weight: bold;", newMsg);
        setMessages((prev) => (prev.some((m) => m.uuid === newMsg.uuid) ? prev : [...prev, newMsg]));
        setConversations((prev) => {
          const updated = prev.map((c) =>
            c.uuid === activeConv.uuid
              ? { ...c, lastMessage: trimmed, lastMessageAt: Math.floor(Date.now() / 1000) }
              : c
          );
          return [...updated].sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
        });
        setTimeout(() => scrollToBottom(false), 20);
      } else {
        setText(trimmed);
      }
    } catch (e) {
      console.error("[CommunicationHub] ❌ Failed to send message:", e);
      setText(trimmed);
    } finally {
      setSending(false);
    }
  };

  // ── Send quick canned response ───────────────────────────────────────────
  const handleQuickResponse = async (quickText) => {
    if (!activeConv || sending) return;
    setSending(true);
    try {
      console.log("%c[CommunicationHub] ⚡ Sending Quick Response:", "color: #8B5CF6; font-weight: bold;", quickText);
      const res = await sendMessageAdmin(activeConv.uuid, {
        messageType: "TEXT",
        content: quickText,
      });
      const newMsg = res?.data?.data;
      if (newMsg) {
        setMessages((prev) => (prev.some((m) => m.uuid === newMsg.uuid) ? prev : [...prev, newMsg]));
        setConversations((prev) => {
          const updated = prev.map((c) =>
            c.uuid === activeConv.uuid
              ? { ...c, lastMessage: quickText, lastMessageAt: Math.floor(Date.now() / 1000) }
              : c
          );
          return [...updated].sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
        });
        setTimeout(() => scrollToBottom(false), 20);
      }
    } catch (e) {
      console.error("[CommunicationHub] ❌ Failed to send canned message:", e);
    } finally {
      setSending(false);
    }
  };

  // ── Upload & send file ───────────────────────────────────────────────────
  const handleFileUpload = async (file) => {
    if (!file || !activeConv) return;

    setUploadProgress("Uploading file...");
    try {
      const formData = new FormData();
      formData.append("file", file);

      const uploadRes = await uploadChatFile(formData);
      const fileData = uploadRes?.data?.data;

      if (!fileData?.url) throw new Error("Upload failed");

      const isImage = file.type.startsWith("image/");
      const fileSizeStr =
        file.size > 1024 * 1024
          ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
          : `${(file.size / 1024).toFixed(1)} KB`;

      setUploadProgress("Sending...");

      const msgRes = await sendMessageAdmin(activeConv.uuid, {
        messageType: isImage ? "IMAGE" : "DOCUMENT",
        fileUrl: fileData.url,
        fileKey: fileData.key,
        fileName: file.name,
        fileSize: fileSizeStr,
        mimeType: file.type,
      });

      const newMsg = msgRes?.data?.data;
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
        setTimeout(() => scrollToBottom(false), 20);
      }
    } catch (e) {
      console.error("File upload failed", e);
      alert("Failed to upload file. Please try again.");
    } finally {
      setUploadProgress(null);
    }
  };

  // ── Drag and Drop handlers ───────────────────────────────────────────────
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  // ── Start conversation from modal ────────────────────────────────────────
  const handleSelectContact = async (type, contact) => {
    setIsModalOpen(false);
    try {
      const payload =
        type === "ADMIN"
          ? { type: "ADMIN", targetAdminId: contact.uuid, targetAdminName: contact.name }
          : { type: "DIRECT", surveyerId: contact.uuid, surveyerName: contact.name };

      const res = await startConversation(payload);
      const conv = res?.data?.data;
      if (conv) {
        setActiveConv(conv);
        loadConversations();
      }
    } catch (err) {
      console.error("Failed to start conversation:", err);
      alert("Failed to start conversation. Please try again.");
    }
  };

  // ── Filtered conversations ───────────────────────────────────────────────
  const filteredConversations = conversations.filter((c) => {
    const nameMatch = (c.surveyerName || "").toLowerCase().includes(search.toLowerCase());
    const surveyMatch = (c.surveyId || "").toLowerCase().includes(search.toLowerCase());
    const textMatch = (c.lastMessage || "").toLowerCase().includes(search.toLowerCase());
    const matchesSearch = nameMatch || surveyMatch || textMatch;

    if (!matchesSearch) return false;

    if (filter === "Survey Jobs") return c.type === "SURVEY" || !!c.surveyId;
    if (filter === "Direct") return c.type === "DIRECT" && !c.surveyId;
    if (filter === "Admin Team") return c.type === "ADMIN";
    if (filter === "Unread") return c.unreadAdminCount > 0;
    return true;
  });

  // ── Render Message Stream grouped by date ────────────────────────────────
  const renderMessageStream = () => {
    if (loading) {
      return (
        <div className="comm-loading-state">
          <div className="comm-spinner" />
          <p>Loading messages...</p>
        </div>
      );
    }

    if (messages.length === 0) {
      return (
        <div className="comm-empty-messages">
          <div className="comm-empty-icon">💬</div>
          <h4>No messages yet</h4>
          <p>Send a message below to start communicating</p>
        </div>
      );
    }

    const elements = [];
    let lastDate = null;

    messages.forEach((msg, idx) => {
      const msgDate = new Date(msg.createdAt * 1000).toDateString();
      if (msgDate !== lastDate) {
        elements.push(<DateSeparator key={`date-${idx}`} unix={msg.createdAt} />);
        lastDate = msgDate;
      }
      elements.push(
        <MessageBubble
          key={msg.uuid || idx}
          msg={msg}
          onImageClick={(src) => setLightboxSrc(src)}
          onDelete={handleDeleteMessage}
        />
      );
    });

    return elements;
  };

  return (
    <div className="comm-hub-page">
      {/* ─── In-App Floating Notification Banner ─────────────────────────── */}
      {inAppBanner && (
        <div
          className="comm-inapp-banner"
          onClick={() => {
            const target = conversations.find((c) => c.uuid === inAppBanner.conversationId);
            if (target) {
              setActiveConv(target);
            } else {
              setActiveConv({
                uuid: inAppBanner.conversationId,
                surveyerName: inAppBanner.senderName,
                type: inAppBanner.role === "Admin" ? "ADMIN" : "DIRECT",
              });
            }
            setInAppBanner(null);
          }}
        >
          <div className="comm-banner-avatar">
            {(inAppBanner.senderName || "S")[0].toUpperCase()}
          </div>
          <div className="comm-banner-content">
            <div className="comm-banner-title-row">
              <span className="comm-banner-name">{inAppBanner.senderName}</span>
              <span className={`comm-role-chip-mini ${inAppBanner.role === "Admin" ? "role-admin" : "role-surveyor"}`}>
                {inAppBanner.role}
              </span>
            </div>
            <div className="comm-banner-body">{inAppBanner.body}</div>
          </div>
          <button
            className="comm-banner-close"
            onClick={(e) => {
              e.stopPropagation();
              setInAppBanner(null);
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ─── Page Header Bar ─────────────────────────────────────────────── */}
      <div className="comm-hub-header">
        <div className="comm-header-left">
          <button
            className="comm-back-nav-btn"
            onClick={() => navigate(-1)}
            title="Go back to previous page"
          >
            ← Back
          </button>
          <div className="comm-header-icon">💬</div>
          <div>
            <h2 className="comm-header-title">Communication Hub</h2>
            <p className="comm-header-sub">
              Real-time messaging with Surveyors and Admin Team
            </p>
          </div>
        </div>

        <div className="comm-header-right">
          {/* Automated Message Button (WhatsApp-style auto reply) */}
          <button
            className="comm-auto-reply-btn"
            onClick={() => setIsAutoReplyModalOpen(true)}
            title="Configure WhatsApp-style Automated Message for Surveyors"
          >
            🤖 Automated Message
          </button>

          {/* Notification Permission Button */}
          {notifPermission !== "granted" ? (
            <button
              className="comm-notif-btn enable"
              onClick={requestNotificationPermission}
              title="Click to enable desktop push notifications"
            >
              🔔 Enable Notifications
            </button>
          ) : (
            <span className="comm-notif-badge-active" title="Desktop notifications are active">
              🔔 Notifications Active
            </span>
          )}

          {/* New Message Button */}
          <button
            className="comm-new-chat-btn"
            onClick={() => setIsModalOpen(true)}
          >
            ➕ New Message
          </button>
        </div>
      </div>

      {/* ─── Main Split-Pane Container ───────────────────────────────────── */}
      <div className="comm-layout-card">
        {/* ── Left Sidebar (Conversations List) ─────────────────────────── */}
        <div className="comm-sidebar">
          {/* Search Box */}
          <div className="comm-search-wrap">
            <input
              type="text"
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="comm-search-input"
            />
          </div>

          {/* Filter Pills */}
          <div className="comm-filter-tabs">
            {["All", "Survey Jobs", "Direct", "Admin Team", "Unread"].map((f) => (
              <button
                key={f}
                className={`comm-filter-pill ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Conversations List */}
          <div className="comm-conv-list">
            {filteredConversations.length === 0 ? (
              <div className="comm-empty-convs">
                <p>No conversations found</p>
                <button
                  className="start-chat-btn-small"
                  onClick={() => setIsModalOpen(true)}
                >
                  Start a new chat
                </button>
              </div>
            ) : (
              filteredConversations.map((conv) => (
                <ConvItem
                  key={conv.uuid}
                  conv={conv}
                  isActive={activeConv?.uuid === conv.uuid}
                  onClick={(c) => setActiveConv(c)}
                  presence={presenceMap[conv.surveyerId || conv.targetAdminId]}
                  onDelete={handleDeleteConversation}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Right Pane (Active Chat Stream) ───────────────────────────── */}
        <div
          className={`comm-chat-pane ${dragOver ? "drag-over" : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {activeConv ? (
            <>
              {/* Chat Top Bar */}
              <div className="comm-chat-topbar">
                <div className="comm-chat-top-left">
                  <button
                    className="comm-chat-back-btn"
                    onClick={() => setActiveConv(null)}
                    title="Back to conversations list"
                  >
                    ← Back
                  </button>
                  {(() => {
                    const isSelf = (activeConv.title || "").toLowerCase().includes("note to self") || (activeConv.title || "").toLowerCase().includes("message yourself");
                    const isSurvey = activeConv.type === "SURVEY" || !!activeConv.surveyId;
                    const isAdmin = activeConv.type === "ADMIN";

                    const topDisplayName = isSelf
                      ? "Note to Self (Mobile App Sync)"
                      : isSurvey
                      ? (activeConv.assignmentName || `Job #${activeConv.surveyId ? (activeConv.surveyId.length > 8 ? activeConv.surveyId.slice(0, 8) + "..." : activeConv.surveyId) : "Survey"}`) + ` · ${activeConv.surveyerName || "Surveyor"}`
                      : isAdmin
                      ? (activeConv.targetAdminName || activeConv.surveyerName || "Admin Member")
                      : (activeConv.surveyerName || "Surveyor");

                    const roleBadge = isSelf
                      ? "📱 Mobile Sync"
                      : isSurvey
                      ? "📋 Survey Job"
                      : isAdmin
                      ? "🛡️ Admin Team"
                      : "👷 Surveyor";

                    const targetUserId = activeConv.surveyerId || activeConv.targetAdminId;
                    const activePresence = targetUserId ? presenceMap[targetUserId] : null;
                    const activeStatus = activePresence?.status || "OFFLINE";

                    return (
                      <>
                        <div style={{ position: "relative" }}>
                          <div
                            className={`comm-chat-avatar ${isSelf ? "" : isAdmin ? "avatar-admin" : isSurvey ? "avatar-survey" : ""}`}
                            style={isSelf ? { background: "#7c3aed", color: "#fff" } : {}}
                          >
                            {isSelf ? "📱" : topDisplayName[0].toUpperCase()}
                          </div>
                          {!isSelf && (
                            <span
                              className={`presence-dot-avatar ${activeStatus.toLowerCase()}`}
                              title={`Status: ${activeStatus}`}
                            />
                          )}
                        </div>
                        <div>
                          <div className="comm-chat-name-row">
                            <span className="comm-chat-name">{topDisplayName}</span>
                            <span
                              className={`comm-role-chip ${isSelf ? "" : isAdmin ? "role-admin" : isSurvey ? "role-survey" : "role-surveyor"}`}
                              style={isSelf ? { background: "#7c3aed", color: "#fff" } : {}}
                            >
                              {roleBadge}
                            </span>
                          </div>
                          {isSelf ? (
                            <div className="comm-chat-sub" style={{ color: "#7c3aed" }}>
                              📱 Note to Self · Synced live with your mobile device
                            </div>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "3px", flexWrap: "wrap" }}>
                              <span className={`presence-pill ${activeStatus.toLowerCase()}`}>
                                <span className={`presence-dot-indicator ${activeStatus.toLowerCase()}`} />
                                {activeStatus === "ACTIVE"
                                  ? "Active in app"
                                  : activeStatus === "IDLE"
                                  ? "Idle · Background"
                                  : activePresence?.lastSeen
                                  ? `Last seen ${formatTime(activePresence.lastSeen)}`
                                  : "Offline"}
                              </span>
                              {activeConv.surveyId && (
                                <span style={{ fontSize: "11.5px", color: "#64748b" }}>
                                  · Job #{activeConv.surveyId.slice(0, 8)}
                                </span>
                              )}
                              {activeConv.assignmentAddress && (
                                <span style={{ fontSize: "11.5px", color: "#0284c7", fontWeight: 600 }}>
                                  · 📍 {activeConv.assignmentAddress}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="comm-chat-top-right">
                  <button
                    type="button"
                    className="comm-chat-delete-btn"
                    onClick={() => handleDeleteConversation(activeConv)}
                    title="Delete this entire conversation"
                  >
                    🗑️ Delete Chat
                  </button>
                  <span className="live-indicator-pill">● Live</span>
                </div>
              </div>

              {/* Shipment Inquiry Detail Context Header */}
              {(activeConv?.type === "SURVEY" || activeConv?.surveyId) && (
                <div style={{
                  background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                  color: "#fff",
                  padding: "10px 18px",
                  borderRadius: "0",
                  borderBottom: "1px solid #334155",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  fontSize: "12.5px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <div style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "8px",
                      background: "rgba(56, 189, 248, 0.15)",
                      border: "1px solid rgba(56, 189, 248, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "16px",
                      flexShrink: 0
                    }}>
                      🚚
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontWeight: 800, color: "#f8fafc", fontSize: "13px" }}>
                          {activeConv.assignmentName || activeConv.title || `Job #${(activeConv.surveyId || "").slice(0, 8)}`}
                        </span>
                        <span style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          padding: "2px 6px",
                          borderRadius: "4px",
                          background: "rgba(56, 189, 248, 0.2)",
                          color: "#38bdf8",
                          border: "1px solid rgba(56, 189, 248, 0.4)"
                        }}>
                          {activeConv.surveyDetail?.status || "JOB INQUIRY"}
                        </span>
                      </div>
                      <div style={{ color: "#94a3b8", fontSize: "11.5px", marginTop: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        📍 {activeConv.assignmentAddress || 
                            (activeConv.surveyDetail?.loadPlaceCity ? `${activeConv.surveyDetail.loadPlaceCity} → ${activeConv.surveyDetail.unloadPlaceCity || 'Destination'}` : 'Route Support')}
                        {activeConv.surveyDetail?.loadDate && ` · 📅 ${new Date(activeConv.surveyDetail.loadDate * 1000).toLocaleDateString()}`}
                      </div>
                    </div>
                  </div>
                  {activeConv.surveyId && (
                    <button
                      type="button"
                      style={{
                        padding: "5px 12px",
                        background: "rgba(255, 255, 255, 0.1)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        color: "#fff",
                        borderRadius: "6px",
                        fontSize: "11.5px",
                        fontWeight: 600,
                        cursor: "pointer",
                        whiteSpace: "nowrap"
                      }}
                      onClick={() => navigate(`/survey/move-checklist/${activeConv.surveyId}/0`)}
                    >
                      View Move Checklist ↗
                    </button>
                  )}
                </div>
              )}

              {/* Upload Progress Indicator */}
              {uploadProgress && (
                <div className="comm-upload-bar">
                  <div className="comm-spinner-small" />
                  <span>{uploadProgress}</span>
                </div>
              )}

              {/* Messages Stream */}
              <div
                className="comm-messages-stream"
                ref={streamContainerRef}
                onScroll={handleStreamScroll}
              >
                {renderMessageStream()}
                {typingUser && (
                  <div className="typing-indicator-pill">
                    <span className="dot dot-1" />
                    <span className="dot dot-2" />
                    <span className="dot dot-3" />
                    <span>{typingUser} is typing...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Scroll to Bottom Floating Button */}
              {showScrollBottom && (
                <button
                  className="comm-scroll-bottom-fab"
                  onClick={() => scrollToBottom(true)}
                  title="Scroll to latest messages"
                >
                  <span className="comm-scroll-icon">↓</span>
                  <span className="comm-scroll-text">New Messages</span>
                </button>
              )}

              {/* Drag and drop overlay hint */}
              {dragOver && (
                <div className="drag-drop-overlay">
                  <div className="drag-drop-box">
                    <span className="drag-icon">📁</span>
                    <p>Drop file here to send</p>
                  </div>
                </div>
              )}

              {/* Canned Quick Responses */}
              <div className="comm-canned-responses">
                <span className="canned-label">Quick:</span>
                {[
                  "Please capture photos of all rooms",
                  "Survey approved — please proceed",
                  "Please take close-up photos of damaged items",
                  "Client has rescheduled — please check updated date",
                  "Report received — great work! 👍",
                ].map((txt, idx) => (
                  <button
                    key={idx}
                    className="comm-canned-pill"
                    onClick={() => handleQuickResponse(txt)}
                    disabled={sending}
                  >
                    {txt}
                  </button>
                ))}
              </div>

              {/* Chat Input Bar */}
              <div className="comm-input-bar">
                {/* Hidden File Inputs */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                  }}
                />
                <input
                  type="file"
                  ref={docInputRef}
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={(e) => {
                    if (e.target.files?.[0]) handleFileUpload(e.target.files[0]);
                  }}
                />

                {/* Attach Photo Button */}
                <button
                  type="button"
                  className="comm-attach-btn"
                  title="Send Image / Photo"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadProgress !== null}
                >
                  📷
                </button>

                {/* Attach Document Button */}
                <button
                  type="button"
                  className="comm-attach-btn"
                  title="Send Document (PDF, Word, Excel)"
                  onClick={() => docInputRef.current?.click()}
                  disabled={uploadProgress !== null}
                >
                  📄
                </button>

                {/* Text Area */}
                <textarea
                  className="comm-text-input"
                  placeholder="Type a message... (Press Enter to send, Shift+Enter for new line)"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendText();
                    }
                  }}
                  rows={1}
                />

                {/* Send Button */}
                <button
                  type="button"
                  className="comm-send-btn"
                  onClick={handleSendText}
                  disabled={!text.trim() || sending}
                >
                  Send ➔
                </button>
              </div>
            </>
          ) : (
            <div className="comm-no-active-chat">
              <div className="comm-empty-illustration">💬</div>
              <h3>Select a Conversation</h3>
              <p>Choose a channel from the left or start a new message</p>
              <button
                className="comm-new-chat-btn"
                style={{ marginTop: "16px" }}
                onClick={() => setIsModalOpen(true)}
              >
                ➕ New Message
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─── Delete Conversation Confirmation Dialog ───────────────────── */}
      <Dialog
        open={Boolean(deleteConvModalTarget)}
        onClose={() => !isDeleting && setDeleteConvModalTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            maxWidth: "420px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px", pb: 1 }}>
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "10px",
              background: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#dc2626",
            }}
          >
            <DeleteOutlineRoundedIcon />
          </div>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "17px", color: "#0f172a" }}>
            Delete Conversation
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
            Are you sure you want to delete this entire conversation with{" "}
            <strong>
              {deleteConvModalTarget?.title ||
                deleteConvModalTarget?.targetAdminName ||
                deleteConvModalTarget?.surveyerName ||
                "this user"}
            </strong>
            ? All messages and shared attachments in this chat will be permanently removed.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: "10px" }}>
          <Button
            onClick={() => setDeleteConvModalTarget(null)}
            disabled={isDeleting}
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteConversation}
            disabled={isDeleting}
            variant="contained"
            sx={{
              background: "#dc2626",
              "&:hover": { background: "#b91c1c" },
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
            }}
          >
            {isDeleting ? "Deleting..." : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Message Confirmation Dialog ────────────────────────── */}
      <Dialog
        open={Boolean(deleteMsgModalTarget)}
        onClose={() => !isDeleting && setDeleteMsgModalTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "8px",
            maxWidth: "380px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px", pb: 1 }}>
          <div
            style={{
              width: "34px",
              height: "34px",
              borderRadius: "10px",
              background: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#dc2626",
            }}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
          </div>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "16px", color: "#0f172a" }}>
            Delete Message
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.6 }}>
            Are you sure you want to delete this message? This action will remove the message permanently for both parties.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: "10px" }}>
          <Button
            onClick={() => setDeleteMsgModalTarget(null)}
            disabled={isDeleting}
            sx={{
              color: "#64748b",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteMessage}
            disabled={isDeleting}
            variant="contained"
            sx={{
              background: "#dc2626",
              "&:hover": { background: "#b91c1c" },
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Lightbox Modal ──────────────────────────────────────────────── */}
      <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />

      {/* ─── New Conversation Modal ─────────────────────────────────────── */}
      <NewChatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelectContact={handleSelectContact}
      />

      {/* ─── Automated Message Modal (Super Admin WhatsApp Auto-Reply) ─── */}
      <AutomatedMessageModal
        isOpen={isAutoReplyModalOpen}
        onClose={() => setIsAutoReplyModalOpen(false)}
      />
    </div>
  );
};

export default CommunicationHub;
